// frontend/src/sections/Home/LatestCompetitions/LatestCompetitions.jsx

import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Icon from "../../../components/common/Icon";
import Button from "../../../components/common/Button";
import GlassCarousel from "../../../components/common/GlassCarousel";
import Reveal from "../../../components/common/Reveal";
import { getPublicMatchesApi } from "../../../api/match.api";

const STATUS = {
  SCHEDULED: {
    label: "برنامه‌ریزی‌ شده",
    className: "bg-blue-50 text-blue-700 border-blue-100",
    dot: "bg-blue-500",
  },
  PLAYED: {
    label: "برگزار شده",
    className: "bg-slate-100 text-slate-600 border-slate-200",
    dot: "bg-slate-400",
  },
  CANCELED: {
    label: "لغو شده",
    className: "bg-red-50 text-red-700 border-red-100",
    dot: "bg-red-500",
  },
  POSTPONED: {
    label: "به تعویق افتاده",
    className: "bg-amber-50 text-amber-700 border-amber-100",
    dot: "bg-amber-500",
  },
};

function getItems(response) {
  const value = response?.data?.data;

  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.items)) return value.items;
  if (Array.isArray(value?.matches)) return value.matches;

  return [];
}

function getStatus(status) {
  return STATUS[status] || STATUS.SCHEDULED;
}

function getDateValue(match) {
  return (
    match?.date ||
    match?.matchDate ||
    match?.startAt ||
    match?.scheduledAt ||
    match?.startTime ||
    null
  );
}

function formatDate(value) {
  if (!value) {
    return {
      day: "--",
      month: "تاریخ نامشخص",
      full: "تاریخ نامشخص",
    };
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return {
      day: "--",
      month: value,
      full: value,
    };
  }

  return {
    day: date.toLocaleDateString("fa-IR", {
      day: "numeric",
    }),
    month: date.toLocaleDateString("fa-IR", {
      month: "long",
    }),
    full: date.toLocaleDateString("fa-IR", {
      weekday: "long",
      day: "numeric",
      month: "long",
      year: "numeric",
    }),
  };
}

function formatTime(value) {
  if (!value) return null;

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return null;

  return date.toLocaleTimeString("fa-IR", {
    hour: "2-digit",
    minute: "2-digit",
  });
}

function getTeam(match, side) {
  if (side === "home") {
    return (
      match?.homeTeam ||
      match?.homeClub ||
      match?.teamA ||
      match?.clubA ||
      match?.home ||
      null
    );
  }

  return (
    match?.awayTeam ||
    match?.awayClub ||
    match?.teamB ||
    match?.clubB ||
    match?.away ||
    null
  );
}

function getTeamName(team) {
  if (!team) return "تیم نامشخص";

  return (
    team?.name ||
    team?.club?.name ||
    team?.title ||
    team?.teamName ||
    "تیم نامشخص"
  );
}

function getTeamLogo(team) {
  if (!team) return null;

  return (
    team?.logo ||
    team?.logoUrl ||
    team?.club?.logo ||
    team?.club?.logoUrl ||
    team?.image ||
    null
  );
}

function getImageUrl(path) {
  if (!path) return null;

  if (
    path.startsWith("http://") ||
    path.startsWith("https://") ||
    path.startsWith("blob:")
  ) {
    return path;
  }

  const base =
    process.env.REACT_APP_API_BASE_URL?.replace("/api/v1", "") || "";

  if (path.startsWith("/")) {
    return `${base}${path}`;
  }

  return `${base}/${path}`;
}

function getCompetitionName(match) {
  return (
    match?.competition?.name ||
    match?.competition?.title ||
    match?.league?.name ||
    match?.league?.title ||
    match?.tournament?.name ||
    match?.tournament?.title ||
    match?.championship?.name ||
    "مسابقات فوتبال شهرستان پاوه"
  );
}

function getVenue(match) {
  return (
    match?.venue?.name ||
    match?.venue ||
    match?.stadium?.name ||
    match?.stadium ||
    match?.field?.name ||
    null
  );
}

function getScore(match, side) {
  if (side === "home") {
    return (
      match?.result?.homeScore ??
      match?.homeScore ??
      match?.score?.home ??
      match?.scores?.home ??
      null
    );
  }

  return (
    match?.result?.awayScore ??
    match?.awayScore ??
    match?.score?.away ??
    match?.scores?.away ??
    null
  );
}

function Team({ team, align = "right" }) {
  const logo = getImageUrl(getTeamLogo(team));
  const name = getTeamName(team);

  return (
    <div
      className={`flex min-w-0 flex-1 items-center gap-2 ${
        align === "left" ? "justify-end" : "justify-start"
      }`}
    >
      {align === "right" && (
        <div className="grid h-12 w-12 shrink-0 place-items-center overflow-hidden rounded-full border border-white bg-white shadow-md transition-all duration-300 group-hover:scale-110">
          {logo ? (
            <img
              src={logo}
              alt={name}
              loading="lazy"
              className="h-full w-full object-contain p-1.5"
            />
          ) : (
            <Icon name="shield" className="h-5 w-5 text-slate-300" />
          )}
        </div>
      )}

      <span
        className={`min-w-0 truncate text-xs font-black text-slate-700 sm:text-sm ${
          align === "left" ? "text-left" : "text-right"
        }`}
      >
        {name}
      </span>

      {align === "left" && (
        <div className="grid h-12 w-12 shrink-0 place-items-center overflow-hidden rounded-full border border-white bg-white shadow-md transition-all duration-300 group-hover:scale-110">
          {logo ? (
            <img
              src={logo}
              alt={name}
              loading="lazy"
              className="h-full w-full object-contain p-1.5"
            />
          ) : (
            <Icon name="shield" className="h-5 w-5 text-slate-300" />
          )}
        </div>
      )}
    </div>
  );
}

function MatchCard({ match }) {
  const status = getStatus(match?.status);
  const date = formatDate(getDateValue(match));
  const time = formatTime(getDateValue(match));

  const homeTeam = getTeam(match, "home");
  const awayTeam = getTeam(match, "away");

  const homeScore = getScore(match, "home");
  const awayScore = getScore(match, "away");

  const venue = getVenue(match);
  const competition = getCompetitionName(match);

  const isLive = false;
  const isFinished = match?.status === "PLAYED";

  return (
    <Link
      to="/competitions"
      className="glass group relative block overflow-hidden rounded-[2rem] p-6 transition-all duration-500 hover:shadow-card-hover"
    >
      {/* Top */}
      <div className="flex items-start justify-between gap-3">
        <span
          className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1.5 text-[10px] font-black ${status.className}`}
        >
          <span
            className={`h-1.5 w-1.5 rounded-full ${status.dot} ${
              isLive ? "animate-pulse" : ""
            }`}
          />

          {status.label}
        </span>

        <div className="text-left">
          <p className="text-[10px] font-bold text-slate-400">
            {date.full}
          </p>

          {time && (
            <p className="mt-0.5 text-[11px] font-black text-slate-600">
              {time}
            </p>
          )}
        </div>
      </div>

      {/* Competition */}
      <div className="mt-5 flex items-center gap-2">
        <span className="grid h-7 w-7 place-items-center rounded-full bg-emerald-500/12 text-emerald-600">
          <Icon name="trophy" className="h-3.5 w-3.5" />
        </span>

        <span className="truncate text-[11px] font-black text-slate-400">
          {competition}
        </span>
      </div>

      {/* Match */}
      <div className="mt-5 flex items-center gap-3">
        <Team team={homeTeam} align="right" />

        <div className="flex shrink-0 flex-col items-center">
          {isFinished || isLive ? (
            <div
              dir="ltr"
              className="flex items-center gap-1.5 rounded-xl bg-ink px-3.5 py-2 text-sm font-black text-floodlight"
            >
              <span>{awayScore ?? "-"}</span>
              <span className="text-white/40">-</span>
              <span>{homeScore ?? "-"}</span>
            </div>
          ) : (
            <div className="grid h-9 min-w-9 place-items-center rounded-full bg-ink px-2.5 text-[10px] font-black text-floodlight">
              VS
            </div>
          )}

          {isLive && (
            <span className="mt-1.5 text-[9px] font-black text-emerald-600">
              در حال برگزاری
            </span>
          )}
        </div>

        <Team team={awayTeam} align="left" />
      </div>

      {/* Venue */}
      {venue && (
        <div className="mt-5 flex items-center gap-2 border-t border-slate-200/70 pt-4">
          <span className="grid h-7 w-7 shrink-0 place-items-center rounded-lg bg-slate-50 text-slate-400">
            <Icon name="map-pin" className="h-3.5 w-3.5" />
          </span>

          <span className="truncate text-[11px] font-medium text-slate-400">
            {venue}
          </span>
        </div>
      )}

      {/* Hover line */}
      <div className="absolute inset-x-0 bottom-0 h-1 origin-right scale-x-0 bg-gradient-to-l from-emerald-500 to-floodlight transition-transform duration-300 group-hover:scale-x-100" />
    </Link>
  );
}

function SkeletonCard() {
  return (
    <div className="glass h-[245px] animate-pulse rounded-[2rem]" />
  );
}

export default function LatestCompetitions() {
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;

    getPublicMatchesApi({
      limit: 6,
      page: 1,
    })
      .then((response) => {
        if (!mounted) return;

        setMatches(getItems(response).slice(0, 6));
      })
      .catch(() => {
        if (mounted) setMatches([]);
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, []);

  const liveMatches = useMemo(
    () => matches.filter((item) => item?.status === "LIVE"),
    [matches]
  );

  return (
    <section className="relative overflow-hidden py-20 sm:py-24 lg:py-28">
      {/* Background decoration */}
      <div className="pointer-events-none absolute -right-40 top-20 h-80 w-80 rounded-full bg-emerald-400/5 blur-3xl" />
      <div className="pointer-events-none absolute -left-40 bottom-10 h-80 w-80 rounded-full bg-lime-400/5 blur-3xl" />

      <div className="container-page relative">
        {/* Header */}
        <div className="flex flex-col justify-between gap-6 lg:flex-row lg:items-end">
          <div>
            <div className="section-eyebrow">
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-60" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" />
              </span>

              مرکز مسابقات فوتبال
            </div>

            <h2 className="section-title mt-5">
              <span className="text-gradient">نبض</span> فوتبال شهرستان پاوه
            </h2>

            <p className="mt-4 max-w-2xl text-sm leading-8 text-slate-500 sm:text-base">
              آخرین مسابقات، نتایج و برنامه رقابت‌ های فوتبال شهرستان پاوه را
              در یک نگاه دنبال کنید.
            </p>

            {liveMatches.length > 0 && (
              <div className="mt-4 inline-flex items-center gap-2 rounded-full bg-floodlight px-4 py-2 text-xs font-black text-ink">
                <span className="h-2 w-2 animate-pulse rounded-full bg-emerald-500" />
                {liveMatches.length.toLocaleString("fa-IR")} مسابقه در حال برگزاری است
              </div>
            )}
          </div>

          <Button
            as={Link}
            to="/competitions"
            variant="outlineDark"
            size="sm"
            className="w-fit shrink-0"
          >
            تقویم کامل مسابقات
            <Icon name="arrowLeft" className="h-4 w-4" />
          </Button>
        </div>

        {/* Cards */}
        {loading ? (
          <div className="mt-12 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
            {[1, 2, 3, 4, 5, 6].map((item) => (
              <SkeletonCard key={item} />
            ))}
          </div>
        ) : matches.length === 0 ? (
          <div className="glass mt-12 overflow-hidden rounded-[2rem] border-dashed px-6 py-20 text-center">
            <div className="mx-auto grid h-16 w-16 place-items-center rounded-2xl bg-white text-slate-300 shadow-sm">
              <Icon name="ball" className="h-8 w-8" />
            </div>

            <h3 className="mt-5 text-lg font-black text-slate-700">
              هنوز مسابقه‌ای ثبت نشده است
            </h3>

            <p className="mx-auto mt-2 max-w-md text-sm leading-7 text-slate-400">
              به محض ثبت مسابقات جدید، برنامه و اطلاعات آن‌ ها
              در این بخش نمایش داده خواهد شد.
            </p>

            <Button
              as={Link}
              to="/competitions"
              variant="outlineDark"
              size="sm"
              className="mt-6"
            >
              مشاهده مرکز مسابقات
              <Icon name="arrowLeft" className="h-4 w-4" />
            </Button>
          </div>
        ) : (
          <Reveal className="mt-14">
            <GlassCarousel>
              {matches.map((match, index) => (
                <MatchCard
                  key={match?.id || match?._id || index}
                  match={match}
                />
              ))}
            </GlassCarousel>
          </Reveal>
        )}

        {/* Bottom link */}
        {!loading && matches.length > 0 && (
          <div className="mt-10 flex justify-center">
            <Link
              to="/competitions"
              className="group inline-flex items-center gap-2 text-sm font-black text-slate-500 transition hover:text-emerald-600"
            >
              مشاهده تمام مسابقات و نتایج

              <span className="grid h-8 w-8 place-items-center rounded-full bg-white/80 transition-all group-hover:bg-ink group-hover:text-floodlight">
                <Icon name="arrowLeft" className="h-4 w-4" />
              </span>
            </Link>
          </div>
        )}
      </div>
    </section>
  );
}